import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import axios from 'axios';
import { useTeam } from './TeamContext';
import { useAuth } from './AuthContext';
import { API_URL } from '../lib/apiUrl';

const defaults = { scope: 'shared', author: '', query: 'workspace=shared', members: [],
  canInspect: false, setScope: () => {}, setAuthor: () => {}, showSaved: () => {} };
const WorkspaceContext = createContext(defaults);
export const useWorkspace = () => useContext(WorkspaceContext);

function readSelection(key) {
  try {
    const stored = JSON.parse(localStorage.getItem('mc_workspace:' + key));
    if (stored && ['mine', 'shared', 'all'].includes(stored.scope)) {
      return { scope: stored.scope, author: typeof stored.author === 'string' ? stored.author : '' };
    }
  } catch {}
  return { scope: 'shared', author: '' };
}

export function WorkspaceProvider({ children }) {
  const { currentTeam } = useTeam();
  const { user } = useAuth();
  const key = `${user?.id || ''}:${currentTeam?.id || ''}`;
  const [selection, setSelection] = useState({});
  const [staff, setStaff] = useState({});
  const cached = useMemo(() => readSelection(key), [key]);
  const scope = selection.key === key ? selection.scope : cached.scope;
  const author = selection.key === key ? selection.author : cached.author;
  const activeStaff = staff.key === key ? staff : {};
  const saveSelection = useCallback(next => {
    try { localStorage.setItem('mc_workspace:' + key, JSON.stringify(next)); } catch {}
    setSelection({ key, ...next });
  }, [key]);
  useEffect(() => {
    if (!currentTeam?.id || !user?.id) return;
    let active = true;
    axios.get(`${API_URL}/api/teams/${currentTeam.id}/staff`).then(({ data }) => {
      if (active) setStaff({ key, ...data });
    }).catch(() => { if (active) setStaff({ key }); });
    return () => { active = false; };
  }, [key, currentTeam?.id, user?.id]);
  const setScope = useCallback(next => saveSelection({ scope: next, author: '' }), [saveSelection]);
  const setAuthor = useCallback(next => saveSelection({ scope, author: next }), [saveSelection, scope]);
  useEffect(() => {
    if (staff.key === key && !staff.can_inspect_private && scope === 'all') setScope('shared');
  }, [staff, key, scope, setScope]);
  const showSaved = useCallback(visibility => setScope(visibility === 'private' ? 'mine' : 'shared'), [setScope]);
  const value = useMemo(() => ({
    scope, author, setScope, setAuthor, showSaved,
    query: new URLSearchParams({ workspace: scope, ...(author ? { author } : {}) }).toString(),
    members: activeStaff.members || [], canInspect: !!activeStaff.can_inspect_private,
  }), [scope, author, setScope, setAuthor, showSaved, activeStaff.members, activeStaff.can_inspect_private]);
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

// Clear stale results synchronously on scope changes; ignore late responses and
// never show one account/team's previous results while fetching another.
export function useWorkspaceResource(url, empty = null) {
  const { user } = useAuth();
  const key = `${user?.id || ''}:${url || ''}`;
  const [revision, setRevision] = useState(0);
  const [result, setResult] = useState({});
  useEffect(() => {
    if (!url) return;
    const controller = new AbortController();
    setResult({ key, loading: true });
    axios.get(url, { signal: controller.signal }).then(({ data }) => {
      if (!controller.signal.aborted) setResult({ key, data, loading: false });
    }).catch(error => {
      if (!controller.signal.aborted) setResult({ key, error, loading: false });
    });
    return () => controller.abort();
  }, [url, key, revision]);
  const refresh = useCallback(() => setRevision(v => v + 1), []);
  const setData = useCallback(update => setResult(prev => prev.key === key
    ? { ...prev, data: typeof update === 'function' ? update(prev.data) : update } : prev), [key]);
  return { data: result.key === key ? result.data ?? empty : empty,
    loading: !!url && (result.key !== key || !!result.loading),
    error: result.key === key ? result.error : null, refresh, setData };
}
